import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@api/client';
import type {
  CreateDocumentSummaryConfigRequest,
  DocumentSummaryConfig,
  DocumentSummaryConfigListResponse,
  NotificationBulkUpdateResponse,
  ReminderSettingsResponse,
  ReminderSettingsUpdate,
  UpdateDocumentSummaryConfigRequest,
} from '@schemas/api';
import { useNotificationStore } from '@store/notificationStore';

const extractErrorMessage = (error: any): string => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0].msg || 'An error occurred';
    }
  }
  return 'An error occurred';
};

export const useReminderSettings = () => {
  return useQuery({
    queryKey: ['settings', 'reminders'],
    queryFn: async () => {
      const { data } = await apiClient.get<ReminderSettingsResponse>('/settings/reminders');
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useUpdateReminderSettings = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: ReminderSettingsUpdate) => {
      const { data } = await apiClient.put<ReminderSettingsResponse>('/settings/reminders', payload);
      return data;
    },
    onSuccess: () => {
      addNotification({
        message: 'Reminder settings updated successfully',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['settings', 'reminders'] });
    },
    onError: (error: any) => {
      addNotification({
        message: extractErrorMessage(error) || 'Failed to update reminder settings',
        type: 'error',
      });
    },
  });
};

export const useDocumentSummaryConfigs = (includeInactive = true) => {
  return useQuery({
    queryKey: ['settings', 'document-summary-configs', includeInactive],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('include_inactive', String(includeInactive));
      params.append('skip', '0');
      params.append('limit', '100');
      const { data } = await apiClient.get<DocumentSummaryConfigListResponse>(
        `/settings/document-summary-configs?${params.toString()}`
      );
      return data;
    },
  });
};

export const useCreateDocumentSummaryConfig = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: CreateDocumentSummaryConfigRequest) => {
      const { data } = await apiClient.post<DocumentSummaryConfig>('/settings/document-summary-configs', payload);
      return data;
    },
    onSuccess: (data) => {
      addNotification({
        message: `Summary config \"${data.name}\" created successfully`,
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['settings', 'document-summary-configs'] });
    },
    onError: (error: any) => {
      addNotification({
        message: extractErrorMessage(error) || 'Failed to create summary config',
        type: 'error',
      });
    },
  });
};

export const useUpdateDocumentSummaryConfig = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: { configId: string; data: UpdateDocumentSummaryConfigRequest }) => {
      const { data } = await apiClient.put<DocumentSummaryConfig>(
        `/settings/document-summary-configs/${payload.configId}`,
        payload.data
      );
      return data;
    },
    onSuccess: (data) => {
      addNotification({
        message: `Summary config \"${data.name}\" updated successfully`,
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['settings', 'document-summary-configs'] });
    },
    onError: (error: any) => {
      addNotification({
        message: extractErrorMessage(error) || 'Failed to update summary config',
        type: 'error',
      });
    },
  });
};

export const useDeleteDocumentSummaryConfig = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (configId: string) => {
      const { data } = await apiClient.delete<NotificationBulkUpdateResponse>(
        `/settings/document-summary-configs/${configId}`
      );
      return data;
    },
    onSuccess: () => {
      addNotification({
        message: 'Summary config deleted successfully',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['settings', 'document-summary-configs'] });
    },
    onError: (error: any) => {
      addNotification({
        message: extractErrorMessage(error) || 'Failed to delete summary config',
        type: 'error',
      });
    },
  });
};
